import { useState, useEffect } from 'react';
import classnames from 'classnames';
import makeStyles from '@material-ui/core/styles/makeStyles';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import _uniq from 'lodash/uniq';
import DialogText from './DialogText';
import { color } from '../../app/theme';
import IcArrowLeft from '../../assets/images/svg/ic-arrow-left.svg';
import IcArrowRight from '../../assets/images/svg/ic-arrow-right.svg';
import IconAddOrg from '../../assets/images/Icons/IconAddOrg';
import IcMoreHorizontal from '../../assets/images/svg/ic-more-horizontal.svg';
import RecurringMessageUploadImage from './RecurringMessageUploadImage';
import { RecurringMessageFrequencyType } from '../../app/types';
import Button from './Button';
import Dropdown from './Dropdown';
import Tooltip from './Tooltip';
import IcTagSvg from '../../assets/images/svg/ic-tag.svg';
import IcHelpCircle from '../../assets/images/svg/ic-help-circle.svg';
import AutoCompletedTextField from './AutoCompletedTextField';
import TextAreaCard from './TextAreaCard';
import { findTag } from '../../features/mainSlice';

const useStyles = makeStyles(
  theme => ({
    contentContainer: {
      height: 651,
      padding: '0 36px 24px',
      overflow: 'auto',
    },
    content: {},
    cardContainer: {
      display: 'flex',
      alignItems: 'flex-start',
      padding: '0 0 8px 0',
      margin: '0 0 16px 0',
      '& > div:last-child': {
        marginRight: 0,
      },
    },
    card: {
      position: 'relative',
      flex: '0 0 auto',
      width: 204,
      borderRadius: 12,
      boxShadow: '0 8px 36px 0 rgba(0, 0, 0, 0.06)',
      backgroundColor: color.$7,
      padding: '12px 0 16px',
      margin: '36px 20px 0 0',
    },
    cardHeader: {
      padding: '0 12px',
    },
    addCard: {
      cursor: 'pointer',
      flex: '0 0 auto',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      width: 205,
      height: 205,
      borderRadius: 12,
      boxShadow: '0 8px 36px 0 rgba(0, 0, 0, 0.06)',
      backgroundColor: color.$7,
      margin: '28px 20px 0 0',
    },
    moveCard: {
      margin: '0 0 8px 0',
      '& > img': {
        cursor: 'pointer',
        display: 'inline-block',
        '&:first-child': {
          marginRight: 8,
        },
      },
    },
    uploadImageContainer: {
      padding: '0 12px',
      marginBottom: 8,
    },
    titleContainer: {
      padding: '0 12px',
      margin: '16px 0 8px',
    },
    divide: {
      margin: '16px 0 4px 0',
      border: 'none',
      borderTop: `1px solid ${color.$9}`,
    },
    addButtonContainer: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      padding: '0 16px',
    },
    addButton: {
      ...theme.link.primaryGreen.default,
      display: 'block',
      fontSize: 13,
      lineHeight: 1,
      margin: '16px 0 0 0',
    },
    moreIcon: {
      cursor: 'pointer',
      position: 'absolute',
      top: -12,
      right: -12,
      zIndex: 1,
      width: 24,
      height: 24,
      borderRadius: '50%',
      backgroundColor: `${color.$10}`,
    },
    moreContainer: showMore => ({
      position: 'absolute',
      top: 28,
      right: 0,
      zIndex: 1,
      display: showMore ? 'block' : 'none',
      width: 160,
      padding: '8px 0',
      backgroundColor: color.$7,
      borderRadius: 12,
      boxShadow:
        '0 16px 48px 0 rgba(0, 0, 0, 0.07), 0 6px 18px 0 rgba(0, 0, 0, 0.03)',
    }),
    moreItem: {
      cursor: 'pointer',
      display: 'flex',
      justifyContent: 'flex-start',
      alignItems: 'center',
      height: 44,
      fontSize: 13,
      lineHeight: '18px',
      padding: '0 24px',
      '&:hover': {
        color: color.$2,
      },
      '& > img': {
        display: 'block',
        margin: '0 0 0 8px',
      },
    },
    actionContainer: {
      display: 'flex',
      justifyContent: 'end',
      alignItems: 'end',
    },
    opertationBlock: {
      display: 'flex',
      justifyContent: 'flex-end',
      alignItems: 'center',
    },
    errorMessage: {
      fontSize: 13,
      color: color.$17,
      textAlign: 'right',
      margin: '0 0 16px 0',
    },
    dropdown: {
      margin: '0 12px 8px',
    },
    frequencyTitle: {
      fontSize: '15px',
      fontWeight: 'normal',
      fontStretch: 'normal',
      fontStyle: 'normal',
      lineHeight: '1',
      color: color.$12,
      margin: '12px 0 8px',
      padding: '0 12px',
      display: 'flex',
      alignItems: 'center',
      '& > img': {
        marginLeft: 4,
      },
    },
    tagContainer: {
      padding: '0 12px',
      display: 'flex',
      alignItems: 'start',
      margin: '8px 0',
      '& > img': {
        display: 'block',
        width: 24,
        height: 24,
        margin: '10px 6px 0 0',
      },
      '& > div': {
        padding: '12px 16px',
      },
      '& textarea': {
        minHeight: 84,
      },
      '& textarea::placeholder': {
        fontSize: '15px',
        fontWeight: 'normal',
        fontStretch: 'normal',
        fontStyle: 'normal',
        lineHeight: 'normal',
        wordBreak: 'break-all',
      },
    },
  }),
  {
    name: 'RecurringMessageDialog',
  },
);

function createDefaultData() {
  return {
    title: '',
    imageUrl: '',
    recurring: {
      frequency: RecurringMessageFrequencyType.DAILY,
      text: 'FREQUENCY',
      reoptin: 'ENABLED',
    },
  };
}

function MoreIcon({ onDelete }) {
  const [showMore, setShowMore] = useState(false);
  const classes = useStyles(showMore);
  const { t } = useTranslation();

  return (
    <ClickAwayListener onClickAway={() => setShowMore(false)}>
      <div
        className={classes.moreIcon}
        onClick={() => {
          setShowMore(!showMore);
        }}
      >
        <img src={IcMoreHorizontal} alt="more" />
        <div className={classes.moreContainer}>
          <div className={classes.moreItem} onClick={onDelete}>
            {t('templates.delete-card')}
          </div>
        </div>
      </div>
    </ClickAwayListener>
  );
}

let findTagTimer = null;

function RecurringMessageDialog({
  orgId,
  disabled,
  elements,
  onSubmit,
  onCancel,
}) {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [isModified, setIsModified] = useState(false);
  const [cardData, setCardData] = useState(
    elements.length > 0 ? [...elements] : [createDefaultData()],
  );
  const candidateTags = useSelector(store => store.main.candidateTags);
  const isLoading = useSelector(store => store.main.isLoading);

  const frequencyItems = [
    {
      text: t('recurringMessage:frequencyItems.DAILY'),
      value: RecurringMessageFrequencyType.DAILY,
    },
    {
      text: t('recurringMessage:frequencyItems.WEEKLY'),
      value: RecurringMessageFrequencyType.WEEKLY,
    },
    {
      text: t('recurringMessage:frequencyItems.MONTHLY'),
      value: RecurringMessageFrequencyType.MONTHLY,
    },
  ];

  function handleAddCard() {
    setCardData([...cardData, createDefaultData()]);
  }

  function handleMoveCardLeft(cardIndex) {
    if (cardIndex !== 0 && cardData.length > 1) {
      const temp = { ...cardData[cardIndex] };
      cardData[cardIndex] = { ...cardData[cardIndex - 1] };
      cardData[cardIndex - 1] = temp;
      setCardData([...cardData]);
    }
  }

  function handleMoveCardRight(cardIndex) {
    if (cardIndex !== cardData.length - 1 && cardData.length > 1) {
      const temp = { ...cardData[cardIndex] };
      cardData[cardIndex] = { ...cardData[cardIndex + 1] };
      cardData[cardIndex + 1] = temp;
      setCardData([...cardData]);
    }
  }

  function handleChangeUploadImage(imageData, cardIndex) {
    setCardData(
      cardData.map((c, i) => {
        if (i === cardIndex) {
          return {
            ...c,
            imageUrl: imageData.imageUrl,
            aspectRatio: imageData.aspectRatio,
          };
        }
        return c;
      }),
    );
    setIsModified(true);
  }

  function handleChangeTitle(title, cardIndex) {
    setCardData(
      cardData.map((c, i) => {
        if (i === cardIndex) {
          return { ...c, title };
        }
        return c;
      }),
    );
    setIsModified(true);
  }

  function handleChangeFrequency(newType, cardIndex) {
    setCardData(
      cardData.map((c, i) => {
        if (i === cardIndex) {
          return {
            ...c,
            recurring: {
              ...c?.recurring,
              frequency: newType,
            },
          };
        }
        return c;
      }),
    );
    setIsModified(true);
  }

  function handleChangeTags(tags, cardIndex) {
    setCardData(
      cardData.map((c, i) => {
        if (i === cardIndex) {
          return { ...c, tags };
        }
        return c;
      }),
    );
    setIsModified(true);
  }

  function handleSubmit() {
    onSubmit(cardData);
  }

  function handleDeleteCard(index) {
    if (cardData.length > 1) {
      const tempCardData = cardData.filter((c, i) => i !== index);
      setCardData(tempCardData);
    }
  }

  const handleTagChange = async value => {
    clearTimeout(findTagTimer);
    findTagTimer = setTimeout(() => {
      dispatch(findTag(value, orgId, undefined, { limit: 20 }));
    }, 1000);
  };

  useEffect(() => {
    dispatch(findTag(undefined, orgId));
  }, []);

  function validateCardData() {
    let totalImages = 0;
    const result = {
      isValid: false,
      message: '',
    };
    for (let i = 0; i < cardData.length; i += 1) {
      const d = cardData[i];

      if (d.title === '' || d.title.length > 65) return result;

      totalImages += d.imageUrl !== '' ? 1 : 0;
    }
    if (totalImages !== 0 && totalImages !== cardData.length) {
      result.message = t('recurringMessage:allSameImageNumber');
      return result;
    }

    const allTitle = cardData.map(c => c?.title);
    if (_uniq(allTitle).length !== allTitle.length) {
      result.message = t('recurringMessage:elementsTitlesRepeatError');
      return result;
    }

    result.isValid = true;
    return result;
  }

  const cardComponents = cardData.map((d, index) => {
    const isTitleRepeat =
      (cardData.filter(c => c?.title === d?.title) || []).length > 1;

    return (
      <div className={classes.card} key={`card-${index}`}>
        {!disabled && (
          <>
            <MoreIcon onDelete={() => handleDeleteCard(index)} />
            <div className={classes.cardHeader}>
              <div className={classes.moveCard}>
                <img
                  src={IcArrowLeft}
                  alt="Move to left"
                  onClick={() => handleMoveCardLeft(index)}
                />
                <img
                  src={IcArrowRight}
                  alt="Move to right"
                  onClick={() => handleMoveCardRight(index)}
                />
              </div>
            </div>
          </>
        )}
        <div className={classes.uploadImageContainer}>
          <RecurringMessageUploadImage
            imageUrl={d.imageUrl}
            index={index}
            elements={cardData}
            onChange={imageData => handleChangeUploadImage(imageData, index)}
            disabled={disabled}
          />
        </div>
        <hr className={classnames(classes.divide)} />
        <div className={classes.titleContainer}>
          <TextAreaCard
            placeholder={t('recurringMessage:elementsTitle')}
            value={d.title}
            onChange={title => handleChangeTitle(title, index)}
            maxCount={65}
            status={
              (isModified && d.title) === '' || isTitleRepeat
                ? 'error'
                : undefined
            }
            disabled={disabled}
          />
        </div>
        <hr className={classnames(classes.divide)} />
        <div className={classes.frequencyTitle}>
          {t('recurringMessage:frequencyTitle')}
          <Tooltip
            light
            placement="top"
            title={t(`recurringMessage:frequencyItemsTooltip`)}
          >
            <img
              className={classes.marginLeft4}
              src={IcHelpCircle}
              alt="alert"
            />
          </Tooltip>
        </div>
        <Dropdown
          isDialog
          className={classes.dropdown}
          menuItems={frequencyItems}
          value={d?.recurring?.frequency}
          onChange={value => handleChangeFrequency(value, index)}
          disabled={disabled}
        />
        <div className={classes.tagContainer}>
          <img src={IcTagSvg} alt="Tag" />
          <AutoCompletedTextField
            placeholder={t('AutoCompletedTextFieldPlaceholder')}
            candidates={candidateTags || []}
            defaultTags={d?.tags}
            type="inline"
            onEnterEvent={tagName => {
              const tagNames = [...d?.tags, tagName];
              handleChangeTags(tagNames, index);
            }}
            onSetTags={tagNames => {
              handleChangeTags(tagNames, index);
            }}
            onChange={handleTagChange}
            isLoading={isLoading}
            disabled={disabled}
            multiline
          />
        </div>
      </div>
    );
  });
  const contentComponent = (
    <div className={classes.content}>
      <div className={classes.cardContainer}>
        {cardComponents}
        {cardComponents.length < 5 && (
          <div className={classes.addCard} onClick={handleAddCard}>
            <IconAddOrg id="add-card-icon" width={46.4} height={46.4} />
          </div>
        )}
      </div>
    </div>
  );
  const { isValid, message: errorMessage } = validateCardData();
  const actionComponent = (
    <div style={{ width: '100%' }}>
      {errorMessage !== '' && (
        <div className={classes.errorMessage}>{errorMessage}</div>
      )}
      <div className={classes.actionContainer}>
        <div className={classes.opertationBlock}>
          <Button topic="primaryBlue" onClick={onCancel}>
            {!disabled ? t('modal.cancel') : t('modal.sure')}
          </Button>
          {!disabled && (
            <Button
              style={{ margin: '0 0 0 8px' }}
              topic="primaryBlueHighLight"
              onClick={handleSubmit}
              isDisable={!isValid}
            >
              {t('modal.sure')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
  return (
    <DialogText
      isOpen
      title={t('recurringMessage:step2')}
      content={contentComponent}
      contentClass={classes.contentContainer}
      actions={actionComponent}
    />
  );
}

export default RecurringMessageDialog;
